import { Box, styled } from '@mui/material';
import { FC } from 'react';
import { FunctionField, SelectField, TextField, TopToolbar } from 'react-admin';

import { GetBoInvestorProfilesEndpoint } from '@Common/types/bo/b2b/investor_profile';

import { investorProfileRiskProfileChoices } from '@sh4res/bo-common/choices/investorProfile';
import { DatagridTyped, List } from '@sh4res/bo-common/components';
import { IdField } from '@sh4res/bo-common/fields';
import { ResourceBasePath, useResourceBasePath, useSelectedColumns } from '@sh4res/bo-common/hooks';
import { B2bInvestorProfileCreate } from '@sh4res/bo-common/resources/investor_profile/B2bInvestorProfileCreate';
import { getB2BInvestorProfileResourceReference } from '@sh4res/bo-common/resources/investor_profile/helpers/getB2BInvestorProfileResourceReference';
import { useB2bCompanyIdGuard } from '@sh4res/bo-common/resources/investor_profile/hooks/useB2bCompanyIdGuard';
import { ColumnList } from '@sh4res/bo-common/utils';

export const b2bInvestorProfileDefaultColumns: (keyof GetBoInvestorProfilesEndpoint['Client']['ResponseBody'][number])[] =
  ['id', 'plan_category', 'name', 'risk_profile', 'allocation_instruments', 'plan_ids'];

export const createB2bInvestorProfileListColumns = (
  resourceBasePath: ResourceBasePath,
): ColumnList<GetBoInvestorProfilesEndpoint['Client']['ResponseBody'][number]> => ({
  id: (
    <IdField
      reference={getB2BInvestorProfileResourceReference(resourceBasePath)}
      data-testid="BoExternalB2bInvestorProfileList_IdField"
    />
  ),
  b2b_contract_id: (
    <IdField
      reference={`${resourceBasePath}contracts`}
      data-testid={'BoExternalB2bInvestorProfileShow_B2bContractIdField'}
    />
  ),
  plan_category: (
    <TextField
      label="resources.bo_common.b2b.investor_profile.list.plan_category"
      data-testid="BoExternalB2bInvestorProfileList_PlanCategoryTextField"
    />
  ),
  name: <TextField data-testid="BoExternalB2bInvestorProfileList_NameTextField" />,
  allocation_instruments: (
    <FunctionField
      label="resources.bo_common.b2b.investor_profile.list.allocation_nbr"
      render={(record: GetBoInvestorProfilesEndpoint['Client']['ResponseBody'][number]) =>
        record.allocation_instruments.length
      }
      data-testid="BoExternalB2bInvestorProfileList_AllocationInstrumentsFunctionField"
    />
  ),
  plan_ids: (
    <FunctionField
      label="resources.bo_common.b2b.investor_profile.list.plan_ids"
      render={(record: GetBoInvestorProfilesEndpoint['Client']['ResponseBody'][number]) =>
        record.plan_ids.length > 0 ? record.plan_ids.join(', ') : '-'
      }
      data-testid="BoExternalB2bInvestorProfileList_PlanIdsFunctionField"
    />
  ),
  risk_profile: (
    <SelectField
      choices={investorProfileRiskProfileChoices}
      data-testid="BoExternalB2bInvestorProfileList_RiskProfileField"
    />
  ),
  allocations: (
    <FunctionField
      render={(record: GetBoInvestorProfilesEndpoint['Client']['ResponseBody'][number]) =>
        record.allocations.length
      }
      data-testid="BoExternalB2bInvestorProfileList_AllocationsFunctionField"
    />
  ),
});

const B2bInvestorProfileActionsItem = styled(Box)`
  margin: ${({ theme }) => theme.spacing(0, 0, 0, 0.5)};
`;

const B2bInvestorProfileListActions = () => (
  <TopToolbar>
    <B2bInvestorProfileActionsItem>
      <B2bInvestorProfileCreate testId="BoExternalB2bInvestorProfileListB2bInvestorProfileCreate" />
    </B2bInvestorProfileActionsItem>
  </TopToolbar>
);

export const B2bInvestorProfileList: FC = () => {
  const resourceBasePath = useResourceBasePath();
  const b2bInvestorProfileListColumns = createB2bInvestorProfileListColumns(resourceBasePath);
  const displayedColumns = useSelectedColumns<
    GetBoInvestorProfilesEndpoint['Client']['ResponseBody'][number]
  >({
    preferences: 'b2b_investor_profile.list.columns',
    defaultColumns: b2bInvestorProfileDefaultColumns,
  });

  const { companyId } = useB2bCompanyIdGuard({ shouldRedirect: true });

  if (companyId === null) {
    return null;
  }

  return (
    <List
      actions={<B2bInvestorProfileListActions />}
      exporter={false}
      empty={false}
      sort={{ field: 'id', order: 'ASC' }}
      filter={{ b2b_company_id: companyId }}>
      <DatagridTyped<GetBoInvestorProfilesEndpoint['Client']['ResponseBody'][number]>
        bulkActionButtons={false}
        displayedColumns={displayedColumns}
        columnRegistry={b2bInvestorProfileListColumns}
        sortableColumns={[]}
      />
    </List>
  );
};
